import { useTenderDeadline } from "../../../../../../hooks/useTenderDeadline";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { InfoIcon } from "lucide-react";
import style from "./AviationActionsTab.module.scss";
import { Aviation } from "../../../../../../services";
import { CURRENCIES } from "../../../../../../mock";
import { numberToFinanceFormat } from "../../../../../../utils";
import CustomButton from "../../../../../custom/custom-button/CustomButton";
import ThreeDots from "../../../../../shared/Loading/ThreeDots";
import CommentIcon from "../../../../../shared/icons/CommentIcon";
import AviationStatusTimeline from "../AviationStatusTimeline";
import AppTooltip from "../../../../../shared/AppTooltip/AppTooltip";
import { TenderDeadlineText } from "../../../../../shared/TenderDeadlineText/TenderDeadlineText";
import OrderActionHint from "../../../../../orders-new/order-info-client/components/tabs/OrderActionHint";
import RebidNotice from "../../../../../orders-new/order-info/components/RebidNotice/RebidNotice";

const EMPTY_VALUE = "\u2014";
const hasValue = (value) => value !== undefined && value !== null && value !== "";
const formatDateTime = (value) => (value ? moment(value).format("DD.MM.YYYY, HH:mm") : EMPTY_VALUE);
const formatDate = (value) => (value ? moment(value).format("DD.MM.YYYY") : EMPTY_VALUE);

const AviationCreatedCard = ({ statusHistory = [], assignmentMethod }) => {
  const { t } = useTranslation();
  const createdEntry = statusHistory[0];
  const titleKey =
    assignmentMethod === "Auction"
      ? "orderInfoClient.orderCreatedCard.auctionTitle"
      : assignmentMethod === "Tender"
        ? "orderInfoClient.orderCreatedCard.tenderTitle"
        : "orderInfoAviation.actions.createdTitle";

  return (
    <section className={style.created_card}>
      <div className={style.created_header}>
        <span className={style.created_title}>{t(titleKey)}</span>
      </div>
      <div className={style.created_row}>
        <span className={style.created_item}>
          <span className={style.created_label}>{t("orderInfoAviation.actions.createdBy")} </span>
          <span className={style.created_value}>{createdEntry?.author || EMPTY_VALUE}</span>
        </span>
        <span className={style.created_item}>
          <span className={style.created_label}>{t("orderInfoAviation.actions.createdAt")} </span>
          <span className={style.created_value}>{formatDate(createdEntry?.createdAt)}</span>
        </span>
      </div>
    </section>
  );
};

const AviationTenderBids = ({
  order,
  items,
  isTenderExpired,
  refreshOrderData,
  onForceUpdate,
}) => {
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);
  const [selectedBidId, setSelectedBidId] = useState(null);
  const [activeCommentId, setActiveCommentId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const ownCompanyId = user?.company?.id || user?.companyId;
  const canAccept = user?.type === "Client" && ownCompanyId === order?.companyId;

  const handleAccept = async () => {
    if (!selectedBidId) return;

    try {
      setIsLoading(true);
      await Aviation.acceptAirTender(selectedBidId);
      toast.success(t("orderInfo.listSuggestedPrices.acceptSuccess"));
      setSelectedBidId(null);
      await refreshOrderData?.();
      onForceUpdate?.();
    } catch (error) {
      console.error(error);
      toast.error(t("orderInfo.listSuggestedPrices.acceptError"));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className={style.bids_card}>
      <div
        className={cn(style.bids_topbar, {
          [style.bids_topbar_flat]: isTenderExpired && !items.length,
        })}
      >
        <span className={style.bids_title}>{t(order?.assignmentMethod === "Auction"
          ? "orderInfoExpeditor.suggestPrice.auctionDeadline" : "orderInfo.suggestPrice.title2")}</span>
        {order?.tenderDateEnd ? (
          <span className={style.deadline}><TenderDeadlineText date={order.tenderDateEnd} /></span>
        ) : null}
      </div>

      {order?.isInRebid === true && !isTenderExpired && (
        <RebidNotice>{t("orderInfoClient.rebid.clientNotice")}</RebidNotice>
      )}

      {items.length ? (
        <>
          <div className={style.bids_head}>
            <span className={style.col_head}>{t("orderInfoAviation.actions.bidCompany")}</span>
            <span className={style.col_head}>{t("orderInfoAviation.actions.bidDate")}</span>
            <span className={style.col_head}>{t("orderInfoAviation.actions.bidAmount")}</span>
            <span className={style.col_head}>{t("orderInfoAviation.actions.bidTerm")}</span>
            <span />
          </div>
          {items.map((bid) => {
            const hasCommentDetails =
              Boolean(bid.comments) || hasValue(bid.weight) || hasValue(bid.volume);
            const isCommentOpen = activeCommentId === bid.id;
            const companyName = bid.expeditorName || bid.userName || EMPTY_VALUE;

            return (
              <React.Fragment key={bid.id}>
                <div className={style.bids_row}>
                  <span
                    className={style.bid_company}
                    data-tooltip-id={`aviation-bid-company-tooltip-${bid.id}`}
                  >
                    {companyName}
                  </span>
                  <span className={style.bid_cell}>
                    {formatDateTime(bid.updatedAt || bid.createdAt)}
                  </span>
                  <span className={style.bid_price}>
                    {numberToFinanceFormat(bid.price)}{" "}
                    {CURRENCIES[bid.currency || "EUR"]?.symbol || ""}
                  </span>
                  <span className={style.bid_term}>
                    <span>
                      {bid.plannedDays
                        ? `${bid.plannedDays} ${t("orderInfoAviation.actions.daysShort")}`
                        : EMPTY_VALUE}
                    </span>
                    {hasCommentDetails ? (
                      <button
                        type="button"
                        className={style.comment_btn}
                        title={t("orderInfoClient.suggestedPrices.commentTitle")}
                        aria-expanded={isCommentOpen}
                        onClick={() =>
                          setActiveCommentId((currentId) =>
                            currentId === bid.id ? null : bid.id,
                          )
                        }
                      >
                        <CommentIcon type={3} fill="#212121" />
                      </button>
                    ) : null}
                  </span>
                  <span className={style.bid_actions}>
                    {canAccept ? (
                      <button
                        type="button"
                        className={style.accept_btn}
                        onClick={() => setSelectedBidId(bid.id)}
                        disabled={isLoading}
                      >
                        {t("orderInfoAviation.actions.accept")}
                      </button>
                    ) : null}
                  </span>
                </div>

                {bid?.isExAccepted === true && (
                  <div className={style.executor_note}>
                    <InfoIcon size={16} color="#22659A" />
                    <span>{t("orderInfoClient.suggestedPrices.orderWithdrawnFromPartner")}</span>
                  </div>
                )}

                <AppTooltip
                  id={`aviation-bid-company-tooltip-${bid.id}`}
                  positionStrategy="fixed"
                  delayShow={50}
                >
                  {companyName}
                </AppTooltip>

                {isCommentOpen && hasCommentDetails ? (
                  <div className={style.comment_box}>
                    <div className={style.comment_box_head}>
                      {t("orderInfoClient.suggestedPrices.commentTitle")}
                    </div>
                    <div className={style.comment_box_body}>
                      {bid.comments ? <span>{bid.comments}</span> : null}
                      {hasValue(bid.weight) ? (
                        <span>
                          <b>{t("orderInfoClient.suggestedPrices.weight")}</b> {bid.weight}
                        </span>
                      ) : null}
                      {hasValue(bid.volume) ? (
                        <span>
                          <b>{t("orderInfoClient.suggestedPrices.volume")}</b> {bid.volume}
                        </span>
                      ) : null}
                    </div>
                  </div>
                ) : null}
              </React.Fragment>
            );
          })}
        </>
      ) : null}

      {!items.length && !isTenderExpired ? (
        <div className={style.no_offers}>
          <span className={style.no_offers_text}>{t("orderInfoAviation.actions.noOffers")}</span>
          <ThreeDots />
        </div>
      ) : null}

      {selectedBidId
        ? ReactDOM.createPortal(
            <div
              className={style.modal_overlay}
              data-portal-modal="true"
              onClick={() => setSelectedBidId(null)}
            >
              <div
                className={style.confirm_modal}
                role="dialog"
                aria-label={t("orderInfo.listSuggestedPrices.confirmMessage")}
                onClick={(event) => event.stopPropagation()}
              >
                <h4 className={style.confirm_title}>{t("orderInfo.listSuggestedPrices.confirmMessage")}</h4>
                <div className={style.modal_actions}>
                  <CustomButton
                    text={t("common.cancel")}
                    isWhite
                    onClick={() => setSelectedBidId(null)}
                    disabled={isLoading}
                  />
                  <CustomButton
                    text={t("common.confirm")}
                    isNavy
                    onClick={handleAccept}
                    isLoading={isLoading}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </section>
  );
};

const AviationActionsTab = ({
  order,
  statusHistory = [],
  suggestedPrices = [],
  refreshOrderData,
  onForceUpdate,
}) => {
  const { isExpired: isTenderExpiredByServer } = useTenderDeadline(order?.tenderDateEnd, { inclusive: true });
  const { t } = useTranslation();
  const user = useSelector((state) => state.user);

  if (!order) return null;

  const ownCompanyId = user?.company?.id || user?.companyId;
  const isOwnerClient = user?.type === "Client" && ownCompanyId === order.companyId;
  const isAwaitingOffer = (order.status || "").toLowerCase() === "awaitingoffer";
  const hasSuggestedPrices = suggestedPrices.length > 0;
  const isTenderExpired = order.tenderDateEnd
    ? isTenderExpiredByServer
    : false;

  if (!isAwaitingOffer || order.assignmentMethod === "DirectAssignment") {
    return (
      <div className={style.wrapper}>
        <AviationStatusTimeline
          order={order}
          statusHistory={statusHistory}
          bids={suggestedPrices}
          refreshOrderData={refreshOrderData}
          onForceUpdate={onForceUpdate}
        />
      </div>
    );
  }

  return (
    <div className={style.wrapper}>
      <AviationCreatedCard statusHistory={statusHistory} assignmentMethod={order.assignmentMethod} />
      <AviationTenderBids
        order={order}
        items={suggestedPrices}
        isTenderExpired={isTenderExpired}
        refreshOrderData={refreshOrderData}
        onForceUpdate={onForceUpdate}
      />
      {isOwnerClient && isTenderExpired && hasSuggestedPrices && (
        <OrderActionHint
          text={t(order.assignmentMethod === "Auction"
            ? "orderInfoClient.suggestedPrices.auctionCompletedSelectWinner"
            : "orderInfoClient.suggestedPrices.tenderCompletedSelectWinner")}
        />
      )}
      {isOwnerClient && isTenderExpired && !hasSuggestedPrices && (
        <OrderActionHint
          text={t(order.assignmentMethod === "Auction"
            ? "orderInfoClient.suggestedPrices.auctionCompletedExtendOrDelete"
            : "orderInfoClient.suggestedPrices.tenderCompletedExtendOrDelete")}
          withoutBorder
        />
      )}
    </div>
  );
};

export default AviationActionsTab;
